import React, { useState } from 'react';
import { 
  Bell, CheckCircle2, HeartPulse, Clock, ShieldCheck, 
  Smile, AlertTriangle, User, Filter 
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA CENTRAL
import { useGlobal } from 'context/GlobalContext';

const CentroNotificaciones = () => {
  const { alertasAdmin } = useGlobal();

  // --- 1. MAPEADO DE ALERTAS REALES ---
  const notificacionesBrutas = alertasAdmin.map(alerta => ({
    id: alerta.id,
    tipoNino: 'hijo', // Lógica base para vista de padres
    nombreNino: alerta.nino,
    tipo: alerta.tipo === 'CONDUCTA' ? 'atencion' : (alerta.tipo === 'positivo' ? 'info' : 'seguridad'),
    titulo: alerta.tipo === 'CONDUCTA' ? 'Aviso de Conducta' : 'Reporte de Salón',
    msj: alerta.reporteMaestro,
    hora: alerta.hora,
    leido: false 
  }));

  // --- 2. FILTRO DE PRIVACIDAD ---
  const notificacionesPermitidas = notificacionesBrutas.filter(noti => {
    if (noti.tipoNino === 'hijo') return true;
    if (noti.tipoNino === 'encargo' && (noti.tipo === 'urgente' || noti.tipo === 'seguridad')) return true;
    return false;
  });

  const [filtroActivo, setFiltroActivo] = useState('TODOS');
  const nombresConAvisos = ['TODOS', ...new Set(notificacionesPermitidas.map(n => n.nombreNino))];

  const notificaciones = notificacionesPermitidas.filter(noti => 
    filtroActivo === 'TODOS' || noti.nombreNino === filtroActivo
  );

  const noLeidas = notificaciones.filter(n => !n.leido).length;

  // --- 3. GESTIÓN DE ESTILOS VISUALES ---
  const obtenerEstiloNotificacion = (tipo) => {
    switch (tipo) {
      case 'urgente':
        return {
          bg: 'bg-rose-100 border-rose-300 shadow-rose-200',
          iconBg: 'bg-rose-600 text-white animate-pulse',
          icon: <AlertTriangle size={28} />, 
          textColor: 'text-rose-900',
          tagColor: 'text-rose-700'
        };
      case 'atencion':
        return {
          bg: 'bg-amber-50 border-amber-300 shadow-amber-100',
          iconBg: 'bg-amber-500 text-white animate-pulse', 
          icon: <HeartPulse size={28} />, 
          textColor: 'text-amber-900',
          tagColor: 'text-amber-700'
        };
      case 'seguridad':
        return {
          bg: 'bg-blue-50 border-blue-200 shadow-blue-100',
          iconBg: 'bg-blue-500 text-white',
          icon: <Smile size={28} />, 
          textColor: 'text-blue-900',
          tagColor: 'text-blue-700'
        };
      case 'info':
      default:
        return {
          bg: 'bg-white border-slate-200 shadow-slate-100',
          iconBg: 'bg-slate-100 text-emerald-500',
          icon: <CheckCircle2 size={28} />,
          textColor: 'text-slate-800',
          tagColor: 'text-slate-500'
        };
    }
  };

  return (
    <div className="w-full max-w-[800px] mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 px-4 text-left font-sans">
      
      {/* ENCABEZADO */}
      <div className="flex justify-between items-end border-b-4 border-slate-200 pb-6">
        <div className="space-y-1">
          <h2 className="text-4xl font-black text-slate-900 tracking-tight italic uppercase leading-none">Tus Avisos</h2>
          <p className="text-base text-slate-500 font-bold uppercase tracking-widest italic">
            Mensajes directos del salón
          </p>
        </div>
        
        {noLeidas > 0 && (
          <div className="flex items-center gap-2 bg-rose-600 text-white px-4 py-2 rounded-xl shadow-md mb-2">
            <Bell size={18} className="animate-bounce" />
            <span className="text-sm font-black tracking-widest uppercase">{noLeidas} Nuevos</span>
          </div>
        )}
      </div>

      {/* BARRA DE FILTROS */}
      {nombresConAvisos.length > 2 && ( 
        <div className="bg-slate-50 p-4 rounded-3xl border-2 border-slate-200 space-y-3">
          <div className="flex items-center gap-2 text-slate-500 ml-2">
            <Filter size={18} />
            <span className="text-sm font-black uppercase tracking-widest italic">Filtrar por explorador:</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {nombresConAvisos.map(nombre => (
              <button
                key={nombre}
                onClick={() => setFiltroActivo(nombre)}
                className={`shrink-0 px-6 py-4 rounded-2xl font-black text-base md:text-lg uppercase transition-all flex items-center gap-2 border-4 
                  ${filtroActivo === nombre 
                    ? 'bg-indigo-600 text-white border-indigo-700 shadow-md scale-105' 
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                  }`}
              >
                {nombre !== 'TODOS' && <User size={20} className={filtroActivo === nombre ? 'text-indigo-200' : 'text-slate-400'}/>}
                {nombre}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LISTA DE MENSAJES */}
      <div className="space-y-6">
        {notificaciones.length === 0 ? (
          <div className="text-center py-16 bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-60">
             <CheckCircle2 size={60} className="mx-auto text-slate-300 mb-4" />
             <p className="text-xl font-black text-slate-500 uppercase italic">
               Sin avisos pendientes
             </p>
          </div>
        ) : (
          notificaciones.map((noti) => {
            const estilo = obtenerEstiloNotificacion(noti.tipo);
            return (
              <div 
                key={noti.id} 
                className={`p-6 md:p-8 rounded-[2.5rem] border-4 shadow-md flex flex-col sm:flex-row gap-6 transition-all relative overflow-hidden ${estilo.bg} ${!noti.leido ? 'scale-100' : 'opacity-70 border-opacity-50'}`}
              >
                {!noti.leido && (
                  <div className="absolute top-6 right-6 w-4 h-4 bg-rose-600 rounded-full animate-pulse shadow-md"></div>
                )}

                <div className={`w-16 h-16 md:w-20 md:h-20 rounded-3xl flex items-center justify-center shrink-0 shadow-inner ${estilo.iconBg}`}>
                  {estilo.icon}
                </div>
                
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${noti.tipoNino === 'hijo' ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}`}>
                      <User size={14} /> {noti.nombreNino}
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500">
                      <Clock size={14} />
                      <span className="text-[10px] font-black uppercase tracking-widest">{noti.hora}</span>
                    </div>
                  </div>

                  <div className="text-left">
                    <h4 className={`text-xl md:text-2xl font-black uppercase tracking-tight mb-2 italic ${estilo.tagColor}`}>
                      {noti.titulo}
                    </h4>
                    <p className={`text-lg font-bold leading-relaxed break-words italic ${estilo.textColor}`}>
                      "{noti.msj}"
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* FOOTER DE SEGURIDAD */}
      <div className="flex items-start gap-4 bg-white p-6 rounded-[2.5rem] border-2 border-slate-100 mt-8 shadow-sm">
        <ShieldCheck size={32} className="text-emerald-500 shrink-0" />
        <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] leading-relaxed text-left">
          Tu seguridad es nuestra prioridad. <br/>
          <span className="text-indigo-500">IgleKids Seguro — Casa de Fe</span>
        </p>
      </div>

    </div>
  );
};

export default CentroNotificaciones;